import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { Spinner } from '@components/ui';
import { useDpoTaskSetEnabledMutation } from '@dieterApi/dpo/useDpoTaskSetEnabledMutation';
import { useDpoTaskSetApprovalMutation } from '@dieterApi/dpo/useDpoTaskSetApprovalMutation';
import { DpoState, DpoTaskResourceType, DpoTaskState, useDpoTasksQuery } from '@dieterApi/dpo/useDpoTasksQuery';

import { Button, Switch, Tooltip } from '@mui/material';
import MUIDataTable, { MUIDataTableProps } from 'mui-datatables';
import { useTranslation } from 'react-i18next';

const STATE_COLORS: Record<DpoState, string> = {
  [DpoState.UNTOUCHED]: 'bg-gray-400', // Gray: Not started
  [DpoState.TOUCHED]: 'bg-red-500', // Red: In progress
  [DpoState.COMPLETE]: 'bg-yellow-500', // Yellow: Done by user, awaiting DPO
  [DpoState.AUDITED]: 'bg-green-500', // Green: Approved by DPO
};

const STATE_LABELS: Record<DpoState, string> = {
  [DpoState.UNTOUCHED]: 'Nicht begonnen',
  [DpoState.TOUCHED]: 'In Bearbeitung',
  [DpoState.COMPLETE]: 'Fertig (nicht abgenommen)',
  [DpoState.AUDITED]: 'Abgenommen',
};

function StatusBadge({ state }: { state: DpoState }) {
  return (
    <Tooltip title={STATE_LABELS[state]}>
      <span className={`inline-block w-4 h-4 rounded-full ${STATE_COLORS[state]}`} />
    </Tooltip>
  );
}

export default function Dpo() {
  const { t } = useTranslation();
  const { user, activeCompany } = useUserWithCompany();
  const companyId = activeCompany?.id;

  const { data, loading, error } = useDpoTasksQuery(companyId);
  const [setEnabled, { loading: mutationLoading }] = useDpoTaskSetEnabledMutation(companyId || '');
  const [setApproval, { loading: approvalLoading }] = useDpoTaskSetApprovalMutation(companyId || '');

  const allTasks = data?.getDpoTasks || [];

  // Filter out experimental and meta-tree topics
  const tasks = allTasks.filter((task) => task.topicId !== 'topic-experimental' && task.topicId !== 'topic-meta');

  const handleToggle = async (taskId: string, currentEnabled: boolean) => {
    if (!companyId) return;
    await setEnabled({
      variables: {
        companyId,
        taskId,
        enabled: !currentEnabled,
      },
    });
  };

  const handleApprove = async (taskId: string) => {
    if (!companyId) return;
    await setApproval({
      variables: {
        companyId,
        taskId,
        approved: true,
      },
    });
  };

  const handleUnapprove = async (taskId: string) => {
    if (!companyId) return;
    await setApproval({
      variables: {
        companyId,
        taskId,
        approved: false,
      },
    });
  };

  // Group tasks by listCategory for collapsible sections
  const questionnaireTasks = tasks.filter((t) => t.type === DpoTaskResourceType.Questionnaire);
  const listItemTasks = tasks.filter((t) => t.type === DpoTaskResourceType.ListItem);

  // Group list items by category
  const listItemsByCategory = listItemTasks.reduce(
    (acc, task) => {
      const category = task.listCategory || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(task);
      return acc;
    },
    {} as Record<string, DpoTaskState[]>
  );

  const columns: MUIDataTableProps['columns'] = [
    {
      name: 'isEnabled',
      label: 'Aktiv',
      options: {
        customBodyRender: (value: boolean, tableMeta) => {
          const taskId = tableMeta.rowData[1] as string;
          return (
            <Switch
              checked={value}
              onChange={() => handleToggle(taskId, value)}
              disabled={mutationLoading}
              size="small"
            />
          );
        },
        filter: true,
        filterType: 'checkbox',
      },
    },
    {
      name: 'id',
      label: 'ID',
      options: {
        display: false,
      },
    },
    {
      name: 'name',
      label: 'Aufgabe',
    },
    {
      name: 'dpoState',
      label: 'Status',
      options: {
        customBodyRender: (value: DpoState) => <StatusBadge state={value} />,
        filter: true,
        filterType: 'multiselect',
        filterOptions: {
          names: Object.values(DpoState),
          renderValue: (v) => STATE_LABELS[v as DpoState],
        },
      },
    },
    {
      name: 'approve',
      label: 'Aktionen',
      options: {
        customBodyRender: (_value: any, tableMeta) => {
          const taskId = tableMeta.rowData[1] as string;
          const dpoState = tableMeta.rowData[3] as DpoState;

          // Show approve button for COMPLETE state (awaiting DPO approval)
          if (dpoState === DpoState.COMPLETE) {
            return (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => handleApprove(taskId)}
                disabled={approvalLoading}
              >
                Abnehmen
              </Button>
            );
          }

          // Show unapprove button for AUDITED state (already approved)
          if (dpoState === DpoState.AUDITED) {
            return (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => handleUnapprove(taskId)}
                disabled={approvalLoading}
              >
                Zurücksetzen
              </Button>
            );
          }

          return null;
        },
        filter: false,
        sort: false,
      },
    },
    {
      name: 'topicTitle',
      label: 'Bereich',
      options: {
        filter: true,
      },
    },
    {
      name: 'updatedAt',
      label: 'Letzte Änderung',
      options: {
        customBodyRender: (value: string | undefined) => {
          if (!value) return '-';
          return new Date(value).toLocaleString('de-DE');
        },
        sortDirection: 'desc',
      },
    },
    {
      name: 'type',
      label: 'Typ',
      options: {
        display: false,
        filter: true,
      },
    },
    {
      name: 'listCategory',
      label: 'Kategorie',
      options: {
        display: false,
        filter: true,
      },
    },
  ];

  const tableOptions: MUIDataTableProps['options'] = {
    filterType: 'multiselect',
    selectableRows: 'none',
    download: false,
    print: false,
    viewColumns: true,
    responsive: 'standard',
    rowsPerPage: 25,
    rowsPerPageOptions: [10, 25, 50, 100],
    sortOrder: {
      name: 'topicTitle',
      direction: 'asc',
    },
  };

  if (!user?.hasFeature('DpoManaged')) {
    return (
      <div className="2xl:container p-6">
        <h3 className="text-xl font-medium">DPO Aufgabenliste</h3>
        <p className="text-gray-500 mt-4">
          DPO-Modus ist für dieses Unternehmen nicht aktiviert.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="2xl:container p-6">
        <h3 className="text-xl font-medium">DPO Aufgabenliste</h3>
        <div className="flex justify-center mt-8">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="2xl:container p-6">
        <h3 className="text-xl font-medium">DPO Aufgabenliste</h3>
        <p className="text-red-500 mt-4">Fehler beim Laden der Aufgaben: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="2xl:container p-6">
      <h3 className="text-xl font-medium mb-4">DPO Aufgabenliste</h3>
      <p className="text-gray-600 mb-6">
        Aktivieren Sie Aufgaben, um sie für den Mandanten sichtbar zu machen.
      </p>

      <MUIDataTable
        title={`Aufgaben (${tasks.length})`}
        data={tasks}
        columns={columns}
        options={tableOptions}
      />
    </div>
  );
}
